import {create} from 'zustand';
import {createJSONStorage, persist} from 'zustand/middleware';

export type Theme = 'light' | 'dark';

interface ThemeState {
  theme: Theme;
  /** True once the persisted value has rehydrated from localStorage. */
  _hasHydrated: boolean;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

/**
 * Persisted UI theme (DERP-142). Default is `dark` so the app keeps its
 * current look for anyone who has never toggled. Persistence mirrors the
 * pattern in `src/store/consent.ts`, but uses the default localStorage
 * adapter — the value is a UI preference, not a consent record.
 *
 * The stored key/shape (`theme-storage` → `{ state: { theme } }`) is read by
 * the pre-hydration inline script in `index.html` to set the `<html>` theme
 * class before first paint. Keep the two in sync.
 */
export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      theme: 'dark',
      _hasHydrated: false,
      setTheme: (theme) => set({theme}),
      toggleTheme: () => set({theme: get().theme === 'dark' ? 'light' : 'dark'}),
    }),
    {
      name: 'theme-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({theme: state.theme}),
      onRehydrateStorage: () => (state) => {
        if (state) state._hasHydrated = true;
      },
    },
  ),
);
